/* QR display-name dialog — optional custom name shown on the Scan2Call QR
   poster and visitor ring page, overriding the email-derived account name.
   Empty input reverts to the default. */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, BadgeCheck, Sparkles } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function QrNameDialog({ open, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [defaultName, setDefaultName] = useState("");
  const [allowed, setAllowed] = useState(true);
  const [price, setPrice] = useState(30000);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/doorbell/display-name`, { withCredentials: true });
        setName(r.data.custom ? r.data.display_name : "");
        setDefaultName(r.data.default_name);
        setAllowed(!!r.data.allowed);
        if (r.data.branding_price_ngn) setPrice(r.data.branding_price_ngn);
      } catch { /* keep blanks */ }
    })();
  }, [open]);

  const save = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/doorbell/display-name`, { display_name: name.trim() }, { withCredentials: true });
      toast.success(r.data.custom
        ? `QR name set to "${r.data.display_name}"`
        : "QR name reset to your account name");
      onSaved?.(r.data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save QR name");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-sm sm:max-w-md" data-testid="qr-name-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight flex items-center gap-2">
            <BadgeCheck className="w-4 h-4 text-emerald-600" /> QR display name
          </DialogTitle>
          <DialogDescription className="font-body text-xs">
            Shown on your printed QR poster and to visitors when they scan —
            great for businesses (e.g. "Havilah Hotels Front Desk"). Your
            existing sticker keeps working; the name updates instantly.
          </DialogDescription>
        </DialogHeader>

        {allowed ? (
        <div>
          <Label className="text-[10px] uppercase tracking-widest font-bold text-gray-500 mb-1 block">Display name</Label>
          <Input
            value={name}
            maxLength={40}
            onChange={(e) => setName(e.target.value)}
            placeholder={defaultName || "Your name"}
            className="rounded-sm h-10 text-sm"
            data-testid="qr-name-input"
          />
          <p className="text-[10px] text-gray-400 font-body mt-1">
            Leave empty to use your account name ({defaultName}). Max 40 characters.
          </p>
        </div>
        ) : (
        <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 flex gap-2" data-testid="qr-name-locked">
          <Sparkles className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
          <p className="text-xs text-amber-900 font-body leading-snug">
            Custom QR names are part of <span className="font-bold">QR branding</span> (₦{price.toLocaleString()} one-time).
            Unlock it with the <span className="font-bold">Brand QR</span> button on your Scan2Call card — you'll get your
            own logo on the poster too.
          </p>
        </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm font-body text-xs" data-testid="qr-name-cancel">
            {allowed ? "Cancel" : "Close"}
          </Button>
          {allowed && (
          <Button onClick={save} disabled={busy} className="bg-black hover:bg-gray-800 text-white rounded-sm font-body text-xs" data-testid="qr-name-save">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Save"}
          </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
